import type { FormInstance } from '@arco-design/web-react';
import get from 'lodash/get';

import { hasConfiguredContainerVolumeFormData, hasConfiguredDataDiskFormData } from '../../../utils/listModuleFormData';
import type { ResourceEditMode, SceneLayoutDeployTypeCode, SkuModuleMeta, SkuResourceModulesState } from '../types';
import { SKU_RESOURCE_MODULES_FIELD } from '../types';

export function isSkuResourceModuleChecked(form: FormInstance, moduleKey: string): boolean {
  const modules = form.getFieldValue(SKU_RESOURCE_MODULES_FIELD) as SkuResourceModulesState | undefined;
  if (!modules || typeof modules !== 'object') return true;
  if (!(moduleKey in modules)) return true;
  return !!modules[moduleKey];
}

export function hasFormFieldValue(values: Record<string, unknown>, fieldPath: string): boolean {
  const value = get(values, fieldPath);
  if (value == null || value === '') return false;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return false;
    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
      try {
        const parsed: unknown = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed.some((item) => {
            if (item == null) return false;
            if (typeof item === 'object') return Object.keys(item as object).length > 0;
            return String(item).trim() !== '';
          });
        }
        if (typeof parsed === 'object' && parsed !== null) {
          return Object.keys(parsed as object).length > 0;
        }
      } catch {
        return trimmed !== '[]' && trimmed !== '{}';
      }
    }
    return true;
  }
  if (Array.isArray(value)) {
    return value.some((item) => {
      if (item == null) return false;
      if (typeof item === 'object') return Object.keys(item as object).length > 0;
      return String(item).trim() !== '';
    });
  }
  if (typeof value === 'object') return Object.keys(value as object).length > 0;
  return String(value).trim() !== '';
}

export function hasModulePresetData(preset: Record<string, unknown> | undefined, meta: SkuModuleMeta | undefined): boolean {
  if (!preset) return false;
  const fields = meta?.presetCheckFields;
  if (!fields?.length) return Object.keys(preset).length > 0;
  return fields.some((field) => hasFormFieldValue(preset, field));
}

function hasListModuleFormData(moduleKey: string, form: FormInstance): boolean {
  if (moduleKey === 'dataDisk') {
    return hasConfiguredDataDiskFormData(form.getFieldValue('dataDisks'));
  }
  if (moduleKey === 'storage') {
    return hasConfiguredContainerVolumeFormData(form.getFieldValue('volumes'));
  }
  return true;
}

/** fixed SKU / SKU 创建：list 模块无数据时不展示整块 */
export function shouldShowFixedSkuListModule(skuType: string | undefined, moduleKey: string, form: FormInstance): boolean {
  if (skuType !== 'fixed') return true;
  return hasListModuleFormData(moduleKey, form);
}

/** SKU 创建：列表模块允许空态配置（对齐直建）；下单 fixed 仍按是否有数据判断 */
export function shouldShowSkuListModule(
  skuType: string | undefined,
  moduleKey: string,
  form: FormInstance,
  resourceEditMode?: ResourceEditMode,
): boolean {
  if ((moduleKey === 'storage' || moduleKey === 'dataDisk') && (!resourceEditMode || resourceEditMode === 'sku-create')) {
    return true;
  }
  return shouldShowFixedSkuListModule(skuType, moduleKey, form);
}

export interface ResolveListModuleVisibleOptions {
  moduleKey: string;
  form: FormInstance;
  paymentTiming?: string;
  skuType?: string;
  resourceEditMode?: ResourceEditMode;
  deployTypeCode?: SceneLayoutDeployTypeCode;
  presetConfigSnapshot?: Record<string, unknown>;
  moduleMeta?: SkuModuleMeta;
}

/**
 * 下单 fixed：计价类存储模块（共享存储卷等）无 SKU 预置数据时不展示整块。
 * 覆盖 AppInstance / ModelInstance / TrainingCluster；其余交付类型走 volumes 表单探测。
 */
export function shouldShowOrderFixedStorageModule(
  resourceEditMode: ResourceEditMode | undefined,
  deployTypeCode: SceneLayoutDeployTypeCode | undefined,
  presetConfigSnapshot: Record<string, unknown> | undefined,
  moduleMeta?: SkuModuleMeta,
  form?: FormInstance,
): boolean {
  const isMaasOrder = resourceEditMode === 'order-fixed' || resourceEditMode === 'order-custom';
  if (!isMaasOrder) return true;
  if (deployTypeCode !== 'AppInstance' && deployTypeCode !== 'ModelInstance' && deployTypeCode !== 'TrainingCluster') {
    return true;
  }
  const meta = moduleMeta ?? { presetCheckFields: ['volumes'] };
  if (hasModulePresetData(presetConfigSnapshot, meta)) return true;
  if (form) {
    const readField = (field: string) =>
      typeof form.getFieldsValue === 'function' ? get(form.getFieldsValue() as Record<string, unknown>, field) : form.getFieldValue(field);
    if (hasFormFieldValue({ _imageStorageVolumes: readField('_imageStorageVolumes') }, '_imageStorageVolumes')) return true;
    if (deployTypeCode === 'AppInstance' && hasFormFieldValue({ _appStorageGroups: readField('_appStorageGroups') }, '_appStorageGroups')) {
      return true;
    }
    if (
      deployTypeCode === 'TrainingCluster' &&
      hasFormFieldValue({ trainingSharedVolumes: readField('trainingSharedVolumes') }, 'trainingSharedVolumes')
    ) {
      return true;
    }
  }
  return false;
}

/** 列表类模块整块（含小标题）是否展示 */
export function resolveListModuleVisible(options: ResolveListModuleVisibleOptions): boolean {
  const { moduleKey, form, paymentTiming, skuType, resourceEditMode, deployTypeCode, presetConfigSnapshot, moduleMeta } = options;

  const isListModule = moduleKey === 'dataDisk' || moduleKey === 'storage';
  if (!isListModule) return true;

  /** MaaS / 训练集群存储走各自 presetCheckFields，勿套用 CI `volumes` 表单探测。 */
  if (
    moduleKey === 'storage' &&
    (deployTypeCode === 'ModelInstance' || deployTypeCode === 'AppInstance' || deployTypeCode === 'TrainingCluster')
  ) {
    return shouldShowOrderFixedStorageModule(resourceEditMode, deployTypeCode, presetConfigSnapshot, moduleMeta, form);
  }

  if (!resourceEditMode || resourceEditMode === 'sku-create') {
    return shouldShowSkuListModule(skuType, moduleKey, form, resourceEditMode);
  }

  if (resourceEditMode === 'order-post-paid') {
    return true;
  }

  if (resourceEditMode === 'order-fixed') {
    if (paymentTiming === 'POST_PAID') return true;
    return hasListModuleFormData(moduleKey, form);
  }

  if (resourceEditMode === 'order-custom') {
    // custom 下单：列表模块始终展示；无 preset/表单数据时为空态可增删改，有值时由 accessPolicy 只读
    return true;
  }

  return true;
}
